<template>
  <div class="fixed w-full flex justify-center z-[9999] bottom" :class="positionClass">
    <transition :enter-active-class="enterAnimation" :leave-active-class="leaveAnimation">
      <v-alert
        v-if="isRendered"
        position="fixed"
        :type="alertStore.type"
        variant="outlined"
        rounded="lg"
        :style="alertStyle"
        :class="xs ? 'max-w-[90%]' : 'max-w-screen-md'"
        @mouseenter="alertStore.pauseAutoClose"
        @mouseleave="alertStore.resumeAutoClose"
      >
        <template #prepend>
          <v-icon size="small" :icon="icon" />
        </template>
        <template v-if="!alertStore.autoClose || alertStore.closable" #append>
          <BtnIcon
            size="small"
            icon="mdi-close"
            :color="closeIconColor"
            @click="alertStore.hideAlert"
          />
        </template>
        <template #title>
          <span class="font-semibold text-sm" :style="{ color: titleColor }">
            {{ alertStore.title }}
          </span>
        </template>
        <template #text>
          <p class="text-sm" :style="{ color: textColor }">
            <span v-html="alertStore.text"></span>
          </p>
          <p
            v-if="alertStore.traceId"
            class="text-xs mt-1 italic text-right mr-1"
            :style="{ color: textColor }"
          >
            Traceid: {{ alertStore.traceId }}
          </p>
        </template>
      </v-alert>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { useCommonAlert } from "@/store/commonAlert";
import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import BtnIcon from "./BtnIcon.vue";

const alertStore = useCommonAlert();
const { xs } = useDisplay();

const isRendered = ref(false);

const enterAnimation = computed(() =>
  alertStore.position === "bottom" ? "slide-in-blurred-bottom" : "slide-in-blurred-top"
);
const leaveAnimation = computed(() =>
  alertStore.position === "bottom" ? "slide-out-blurred-bottom" : "slide-out-blurred-top"
);
const positionClass = computed(() => (alertStore.position === "bottom" ? "bottom-44" : "top-10"));

const alertTypeMap = {
  success: {
    bg: "#DCFCE7",
    border: "#22C55E",
    title: "#1D5622",
    text: "#1D5622",
    closeIconColor: "#1D5622",
    icon: "mdi-check-circle",
  },
  warning: {
    bg: "#FFEFDC",
    border: "#D48323",
    title: "#8E4D00",
    text: "#8E4D00",
    closeIconColor: "#8E4D00",
    icon: "mdi-alert",
  },
  error: {
    bg: "#FFECED",
    border: "#DC2626",
    title: "#6B060D",
    text: "#6B060D",
    closeIconColor: "#6B060D",
    icon: "mdi-alert-circle",
  },
  info: {
    bg: "#F2F8FF",
    border: "primary",
    title: "#122C4A",
    text: "#122C4A",
    closeIconColor: "#122C4A",
    icon: "mdi-alert-circle",
  },
};

const alertStyle = computed(() => {
  const c = alertTypeMap[alertStore.type];
  return {
    backgroundColor: c.bg,
    borderColor: c.border,
  };
});
const titleColor = computed(() => alertTypeMap[alertStore.type].title);
const textColor = computed(() => alertTypeMap[alertStore.type].text);
const icon = computed(() => alertTypeMap[alertStore.type].icon);
const closeIconColor = computed(() => alertTypeMap[alertStore.type].closeIconColor);

watch(
  () => alertStore.isVisible,
  (visible) => {
    if (visible) {
      isRendered.value = true;
    } else {
      if (alertStore.autoClose) {
        setTimeout(() => {
          isRendered.value = false;
        }, 500);
      } else {
        isRendered.value = false;
      }
    }
  }
);
</script>

<style scoped>
:deep(.v-alert__prepend) {
  margin-inline-end: 8px;
}
:deep(.v-alert__append) {
  margin-block-start: -8px;
  margin-inline-start: 20px;
}

.slide-in-blurred-top {
  animation: slide-in-blurred-top 0.6s cubic-bezier(0.23, 1, 0.32, 1) both;
}
.slide-out-blurred-top {
  animation: slide-in-blurred-top 0.6s cubic-bezier(0.23, 1, 0.32, 1) reverse both;
}
@keyframes slide-in-blurred-top {
  0% {
    transform: translateY(-1000px) scaleY(2.5) scaleX(0.2);
    transform-origin: 50% 0%;
    filter: blur(40px);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scaleY(1) scaleX(1);
    transform-origin: 50% 50%;
    filter: blur(0);
    opacity: 1;
  }
}

.slide-in-blurred-bottom {
  animation: slide-in-blurred-bottom 0.6s cubic-bezier(0.23, 1, 0.32, 1) both;
}
.slide-out-blurred-bottom {
  animation: slide-in-blurred-bottom 0.6s cubic-bezier(0.23, 1, 0.32, 1) reverse both;
}
@keyframes slide-in-blurred-bottom {
  0% {
    transform: translateY(1000px) scaleY(2.5) scaleX(0.2);
    transform-origin: 50% 100%;
    filter: blur(40px);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scaleY(1) scaleX(1);
    transform-origin: 50% 50%;
    filter: blur(0);
    opacity: 1;
  }
}
</style>
